import React, { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { adjustCredits, fetchPersonDetail, updatePersonNote } from "../../lib/schools";
import { formatApiError } from "../../lib/api";
import {
  buildCreditAdjustWhatsAppMessage,
  buildPersonSheetWhatsAppGreeting,
  openFeeWhatsApp,
  phoneToWaMe,
} from "../../lib/schoolFeeMessages";
import FormLabel from "../FormLabel";
import { FieldHint } from "../createEvent/CreateEventFormUi";
import { HostNoteTextarea } from "../HostNoteField";
import { ReadableNavButton, StickerButton } from "../Sticker";
import WhatsAppFollowUpPrompt from "../WhatsAppFollowUpPrompt";
import {
  WorkflowModalShell,
  WorkflowModalHeader,
  WorkflowModalBody,
} from "../workflow/WorkflowModal";

const PERSON_SHEET_MAX_WIDTH = "sm:max-w-[440px] sm:w-[min(440px,calc(100vw-2rem))]";

function personInitial(name) {
  const ch = String(name || "?").trim().charAt(0);
  return ch ? ch.toUpperCase() : "?";
}

export function CreditAdjustModal({ schoolId, schoolName = "", person, onClose, onDone }) {
  const dialogRef = useRef(null);
  const [delta, setDelta] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [followUp, setFollowUp] = useState(null);
  const organizerId = person?.id || person?.organizer_id;

  async function submit() {
    if (!organizerId || delta === 0) return;
    setBusy(true);
    try {
      const result = await adjustCredits(schoolId, {
        organizer_id: organizerId,
        delta,
        note: note.trim() || undefined,
      });
      toast.success(
        delta > 0
          ? `Granted ${delta} credit${delta === 1 ? "" : "s"}`
          : `Removed ${Math.abs(delta)} credit${Math.abs(delta) === 1 ? "" : "s"}`,
      );
      onDone?.(result);
      setFollowUp({
        delta,
        phone: person?.phone,
        name: person?.name,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (followUp) {
    return (
      <WhatsAppFollowUpPrompt
        title="Credits updated"
        description={`Optionally tell ${followUp.name || "the student"} about the change.`}
        phone={followUp.phone}
        message={buildCreditAdjustWhatsAppMessage({
          studentName: followUp.name,
          schoolName,
          delta: followUp.delta,
        })}
        onClose={onClose}
        testId="credit-adjust-whatsapp"
      />
    );
  }

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={onClose}
      onBackdropClose={onClose}
      testId="credit-adjust-modal"
      titleId="credit-adjust-title"
      layout="sheet"
      maxWidthClass={PERSON_SHEET_MAX_WIDTH}
    >
      <WorkflowModalHeader
        sticky
        eyebrow={person?.name || "Student"}
        title="Adjust credits"
        titleId="credit-adjust-title"
        onClose={onClose}
      />
      <WorkflowModalBody sheet className="space-y-4">
        <FieldHint>
          Fix a wrong Fees tap, or grant a promo pack. One credit usually covers one class.
        </FieldHint>
        <div className="flex items-center justify-center gap-3">
          <StickerButton
            type="button"
            color="white"
            onClick={() => setDelta((d) => d - 1)}
            data-testid="credit-minus"
            aria-label="Decrease credits"
          >
            −
          </StickerButton>
          <input
            type="number"
            className="field w-24 text-center font-semibold text-[18px] min-h-[44px]"
            value={delta}
            onChange={(e) => setDelta(Number(e.target.value) || 0)}
            data-testid="credit-delta"
            aria-label="Credit change"
          />
          <StickerButton
            type="button"
            color="white"
            onClick={() => setDelta((d) => d + 1)}
            data-testid="credit-plus"
            aria-label="Increase credits"
          >
            +
          </StickerButton>
        </div>
        <label className="block">
          <FormLabel className="block">Note</FormLabel>
          <input
            className="field mt-2 min-h-[44px]"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional — e.g. promo, correction"
          />
        </label>
      </WorkflowModalBody>
      <div className="sticky bottom-0 z-10 flex justify-end gap-2 border-t border-black/[0.08] bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <ReadableNavButton type="button" onClick={onClose}>
          Cancel
        </ReadableNavButton>
        <ReadableNavButton
          type="button"
          variant="primary"
          disabled={busy || delta === 0}
          onClick={submit}
          data-testid="credit-confirm"
        >
          {busy ? "Saving…" : "Confirm"}
        </ReadableNavButton>
      </div>
    </WorkflowModalShell>
  );
}

function activityHint(status, isTeam) {
  if (isTeam) {
    return status === "ACTIVE" ? "Currently on the team" : "Invited or no longer on staff";
  }
  return status === "ACTIVE"
    ? "Has credits or an active membership"
    : "No credits left";
}

function roleLabel(membership) {
  if (!membership?.role) return "Student";
  return membership.role === "ADMIN" ? "Admin" : "Instructor";
}

function formatMemberSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function danceRoleLabel(role) {
  const r = String(role || "").trim().toLowerCase();
  if (r === "leader" || r === "lead") return "Leader";
  if (r === "follower" || r === "follow") return "Follower";
  if (r === "both") return "Both";
  return role || null;
}

function packLine(pack) {
  if (pack.kind === "TIME_PASS") {
    return pack.label || "Membership pass";
  }
  const remaining = pack.credits_remaining;
  const total = pack.credits_total;
  if (remaining != null && total != null) {
    return `${pack.label || "Class pack"} (${remaining}/${total} remaining)`;
  }
  if (remaining != null) {
    return `${pack.label || "Class pack"} (${remaining} remaining)`;
  }
  return pack.label || "Class pack";
}

export function PersonDetailSheet({
  schoolId,
  schoolName = "",
  person,
  refreshKey = 0,
  dismissOnEscape = true,
  onClose,
  onAdjustCredits,
  onRoleChange,
  onNoteSaved,
  busy = false,
}) {
  const dialogRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState(person?.note || "");
  const [noteBusy, setNoteBusy] = useState(false);
  const organizerId = person?.id || person?.organizer_id;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await fetchPersonDetail(schoolId, organizerId);
        if (!cancelled) {
          setDetail(data);
          setNote(data?.person?.note || "");
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId, organizerId, refreshKey]);

  useEffect(() => {
    if (person?.credit_balance == null) return;
    setDetail((cur) => (
      cur && cur.credit_balance !== person.credit_balance
        ? { ...cur, credit_balance: person.credit_balance }
        : cur
    ));
  }, [person?.credit_balance]);

  const phone = detail?.person?.phone || person?.phone;
  const name = detail?.person?.name || person?.name || "Person";
  const activity = detail?.person?.activity || person?.activity;
  const activityLabel = detail?.person?.activity_label || person?.activity_label || "Inactive";
  const membership = detail?.person?.membership || person?.membership || null;
  const isTeam = Boolean(membership);
  const creditBalance = detail?.credit_balance ?? person?.credit_balance ?? 0;
  const savedNote = detail?.person?.note || "";
  const noteDirty = note.trim() !== (savedNote || "").trim();
  const roleSelectValue = membership?.role || "STUDENT";
  const hasPhone = Boolean(phoneToWaMe(phone));
  const isActive = activity === "ACTIVE";
  const memberSinceLabel = formatMemberSince(detail?.person?.member_since);
  const danceRole = danceRoleLabel(detail?.person?.dance_role);
  const activePacks = detail?.active_packs || [];
  const attendance = detail?.attendance || {
    checked_in: 0,
    late_cancellations_this_month: 0,
    no_shows: 0,
  };

  function messageWhatsApp() {
    const ok = openFeeWhatsApp({
      phone,
      message: buildPersonSheetWhatsAppGreeting({
        studentName: name,
        schoolName,
      }),
    });
    if (!ok) toast.error("No phone on file");
  }

  async function saveNote() {
    setNoteBusy(true);
    try {
      const result = await updatePersonNote(schoolId, organizerId, note);
      setDetail((cur) => (cur ? {
        ...cur,
        person: { ...cur.person, note: result.note },
      } : cur));
      toast.success(result.note ? "Note saved" : "Note cleared");
      onNoteSaved?.(result.note);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNoteBusy(false);
    }
  }

  function handleRoleSelect(e) {
    const value = e.target.value;
    if (value === roleSelectValue) return;
    onRoleChange?.(value);
  }

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={onClose}
      onBackdropClose={onClose}
      dismissOnEscape={dismissOnEscape}
      testId="person-detail-sheet"
      titleId="person-detail-title"
      layout="sheet"
      maxWidthClass={PERSON_SHEET_MAX_WIDTH}
    >
      <WorkflowModalHeader
        sticky
        align="center"
        title={name}
        titleId="person-detail-title"
        onClose={onClose}
        leading={(
          <div
            className="w-10 h-10 rounded-full bg-night text-cream font-ui font-semibold text-[15px] flex items-center justify-center"
            aria-hidden
          >
            {personInitial(name)}
          </div>
        )}
        subtitle={(
          <p
            className="flex items-center gap-1.5 truncate"
            title={activityHint(activity, isTeam)}
          >
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                isActive ? "bg-emerald-600" : "bg-ink/30"
              }`}
              aria-hidden
            />
            <span className="truncate">
              {memberSinceLabel ? `Member since ${memberSinceLabel}` : activityLabel}
              {" · "}
              {roleLabel(membership)}
            </span>
          </p>
        )}
        actions={(
          <button
            type="button"
            className="rounded-md2 border border-black/[0.08] p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-cream disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2"
            onClick={messageWhatsApp}
            disabled={!hasPhone || loading}
            aria-label={hasPhone ? "Message on WhatsApp" : "No phone on file"}
            title={hasPhone ? "Message on WhatsApp" : "No phone on file"}
            data-testid="person-message"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}
      />

      <WorkflowModalBody sheet className="flex flex-col gap-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {loading ? (
          <div className="animate-pulse motion-reduce:animate-none space-y-3" aria-busy="true">
            <div className="h-20 rounded-md2 bg-black/[0.06]" />
            <div className="h-11 rounded-md2 bg-black/[0.06]" />
            <div className="h-24 rounded-md2 bg-black/[0.06]" />
            <p className="sr-only">Loading…</p>
          </div>
        ) : detail ? (
          <>
            <section className="space-y-2" aria-labelledby="person-contact-heading">
              <h3
                id="person-contact-heading"
                className="font-ui text-[12px] font-semibold uppercase tracking-wide text-ink/60"
              >
                Contact & details
              </h3>
              <dl className="space-y-1.5 text-[14px]">
                <div className="flex justify-between gap-3">
                  <dt className="text-ink/60">WhatsApp no</dt>
                  <dd className="font-medium text-right" data-testid="person-phone">
                    {phone || "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-ink/60">Dance role</dt>
                  <dd className="font-medium text-right" data-testid="person-dance-role">
                    {danceRole || "—"}
                  </dd>
                </div>
              </dl>
            </section>

            <section
              className="mt-4 pt-4 border-t border-black/[0.08] space-y-2"
              aria-labelledby="person-packs-heading"
            >
              <div className="flex items-center justify-between gap-3">
                <h3
                  id="person-packs-heading"
                  className="font-ui text-[12px] font-semibold uppercase tracking-wide text-ink/60"
                >
                  Active packs
                </h3>
                <ReadableNavButton
                  type="button"
                  compact
                  className="shrink-0"
                  onClick={() => onAdjustCredits?.(person)}
                  data-testid="person-adjust-credits"
                >
                  Edit credits
                </ReadableNavButton>
              </div>
              {activePacks.length === 0 ? (
                <FieldHint data-testid="person-packs-empty">
                  No active packs. Balance: {creditBalance} credit{creditBalance === 1 ? "" : "s"}.
                </FieldHint>
              ) : (
                <ul className="space-y-1.5" data-testid="person-packs-list">
                  {activePacks.map((pack) => (
                    <li key={pack.id} className="font-ui text-[14px] text-ink">
                      {packLine(pack)}
                    </li>
                  ))}
                </ul>
              )}
              <p className="font-ui text-[13px] text-ink/60 tabular-nums">
                Total credits: {creditBalance}
              </p>
            </section>

            <section
              className="mt-4 pt-4 border-t border-black/[0.08] space-y-2"
              aria-labelledby="person-attendance-heading"
            >
              <h3
                id="person-attendance-heading"
                className="font-ui text-[12px] font-semibold uppercase tracking-wide text-ink/60"
              >
                Operational attendance log
              </h3>
              <ul className="space-y-1.5 text-[14px]" data-testid="person-attendance-log">
                <li>Checked in: {attendance.checked_in} class{attendance.checked_in === 1 ? "" : "es"}</li>
                <li>
                  Late cancellations: {attendance.late_cancellations_this_month} this month
                  {attendance.late_cancellations_this_month > 0 ? " ⚠️" : ""}
                </li>
                <li>No-shows: {attendance.no_shows}</li>
              </ul>
            </section>

            <div className="mt-4 pt-4 pb-4 border-t border-black/[0.08]">
              <label className="flex items-center justify-between gap-3 min-h-[44px] min-w-0">
                <FormLabel className="shrink-0 mb-0">Team role</FormLabel>
                <select
                  className="field !w-[160px] max-w-[min(160px,100%)] shrink-0 min-h-[40px] text-[13px]"
                  value={roleSelectValue}
                  disabled={busy}
                  onChange={handleRoleSelect}
                  data-testid="person-role-select"
                  aria-describedby="person-role-hint"
                >
                  {!membership ? (
                    <option value="STUDENT">Student</option>
                  ) : null}
                  <option value="INSTRUCTOR">Instructor</option>
                  <option value="ADMIN">Admin</option>
                  {membership ? (
                    <option value="REMOVE">Remove from team</option>
                  ) : null}
                </select>
              </label>
              <FieldHint id="person-role-hint" className="mt-2">
                {isTeam
                  ? "Instructors teach; admins also manage Fees, People, and Studio."
                  : isActive
                    ? "Active means they still have credits or a membership."
                    : "Inactive means no credits left. Adjust credits above, or promote if they join your team."}
              </FieldHint>
            </div>

            <div className="pt-4 border-t border-black/[0.08]">
              <HostNoteTextarea
                label="Admin notes"
                value={note}
                onChange={setNote}
                placeholder="Add a private note about this dancer…"
                className="mt-2 min-h-[88px] resize-y"
                maxLength={500}
                testId="person-note"
              />
              {noteDirty ? (
                <ReadableNavButton
                  type="button"
                  className="mt-2"
                  disabled={noteBusy}
                  onClick={saveNote}
                  data-testid="person-note-save"
                >
                  {noteBusy ? "Saving…" : "Save note"}
                </ReadableNavButton>
              ) : null}
            </div>
          </>
        ) : (
          <FieldHint>Couldn’t load this person. Close and try again.</FieldHint>
        )}
      </WorkflowModalBody>
    </WorkflowModalShell>
  );
}
